/**
 * The task pocket's comment composer (spec §6.5, item 6): the tray composer
 * recipe — a 36px capsule on the field fill without a border, 24px radius
 * once it grows — with today's posting, pasted uploads, @agent mentions and
 * dictation.
 */
import { useCallback, useMemo, useRef, useState } from "react";
import type {
  ChangeEvent,
  Dispatch,
  KeyboardEvent,
  SetStateAction,
} from "react";
import { postComment, type TaskComment } from "@/api/tasks";
import {
  Button,
  CONCEPT_ICONS,
  Icons,
  surfaceRoleAttributes,
  surfaceRoleClassName,
} from "@/components/ui";
import { AgentAvatarPreview } from "@/components/ui/agent-avatar-preview";
import {
  ImagePreviewStrip,
  type ImageAttachment,
} from "@/components/panels/image-preview-strip";
import { useCommentDictationClaim } from "@/desktop/use-comment-dictation-claim";
import { useDictation } from "@/desktop/use-dictation";
import { useAuth } from "@/hooks/use-auth";
import { denkerAvatarColor } from "@/hooks/use-denker-avatar-color";
import { cn } from "@/lib/cn";
import {
  attachComposerFile,
  removeComposerAttachment,
} from "@/lib/composer-attachment";
import type { AgentAvatarStyleKey } from "@/lib/dicebear-styles";
import { useAgentStore } from "@/stores/agent-store";
import { translate, useT } from "@/i18n";
import { panelsTaskCommentsMessages } from "@/i18n/messages/panels-task-comments";
import { useNotificationStore } from "@/stores/notification-store";
import { useProjectStore } from "@/stores/project-store";
import { dedupeTaskComments } from "./task-comment-dedupe";
import type { AgentOption } from "./task-comment-section-types";
import { useComposerFileDrop } from "./use-composer-file-drop";
import { useComposerAttachmentLifetime } from "./use-composer-attachment-lifetime";

const MAX_ATTACHMENTS = 5;
const MAX_FIELD_HEIGHT = 120;
/** One 13px line at 1.4 line height; a taller field has grown. */
const ONE_LINE_HEIGHT = 20;

export function TaskCommentComposer({
  projectId,
  taskId,
  comments,
  onCommentsChange,
}: {
  projectId: string;
  taskId: string;
  comments: TaskComment[];
  onCommentsChange: (comments: TaskComment[]) => void;
}) {
  const t = useT(panelsTaskCommentsMessages);
  const projectAgents = useAgentStore((s) => s.agents);
  const project = useProjectStore((s) =>
    s.projects.find((candidate) => candidate.id === projectId),
  );
  const { user } = useAuth();
  const avatarStyleKey = (user?.agent_avatar_style ??
    "glass") as AgentAvatarStyleKey;
  const dictationStatus = useDictation((s) => s.status);
  const [draft, setDraft] = useState("");
  const [posting, setPosting] = useState(false);
  const postingRef = useRef(false);
  const [grown, setGrown] = useState(false);
  const [pendingImages, setPendingImages] = useState<ImageAttachment[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState("");
  const [mentionIndex, setMentionIndex] = useState(0);
  const [mention, setMention] = useState<AgentOption | null>(null);
  const fieldRef = useRef<HTMLTextAreaElement>(null);
  const composerRef = useRef<HTMLDivElement>(null);
  const draftRef = useRef(draft);
  draftRef.current = draft;
  const pendingImagesRef = useRef(pendingImages);
  pendingImagesRef.current = pendingImages;
  const commentsRef = useRef(comments);
  commentsRef.current = comments;
  const mentionRef = useRef(mention);
  mentionRef.current = mention;
  // Reserve dropped files synchronously: a batch may arrive before React
  // renders, and a pending upload must block Enter in that same event turn.
  const updateAttachments = useCallback<
    Dispatch<SetStateAction<ImageAttachment[]>>
  >((update) => {
    const next =
      typeof update === "function" ? update(pendingImagesRef.current) : update;
    pendingImagesRef.current = next;
    setPendingImages(next);
  }, []);
  useComposerAttachmentLifetime(pendingImages, updateAttachments);

  const agentOptions: AgentOption[] = useMemo(
    () =>
      projectAgents.map((agent) => ({
        id: agent.agent_template,
        name: agent.template_name || t("agent"),
        avatar_seed: agent.template_avatar_seed,
      })),
    [projectAgents, t],
  );
  const filteredAgents = useMemo(() => {
    if (!mentionFilter) return agentOptions;
    const query = mentionFilter.toLowerCase();
    return agentOptions.filter((agent) =>
      agent.name.toLowerCase().includes(query),
    );
  }, [agentOptions, mentionFilter]);

  const resizeField = useCallback((field: HTMLTextAreaElement) => {
    field.style.height = "auto";
    field.style.height = `${Math.min(field.scrollHeight, MAX_FIELD_HEIGHT)}px`;
    setGrown(field.scrollHeight > ONE_LINE_HEIGHT);
  }, []);

  const resetField = useCallback(() => {
    if (fieldRef.current) fieldRef.current.style.height = "auto";
    setGrown(false);
  }, []);

  const selectMention = useCallback((agent: AgentOption) => {
    setMention(agent);
    setShowMentions(false);
    setMentionFilter("");
    setDraft((previous) => previous.replace(/@\S*$/, "").trimEnd());
    fieldRef.current?.focus();
  }, []);

  const removeMention = useCallback(() => {
    setMention(null);
    fieldRef.current?.focus();
  }, []);

  const handleFile = useCallback(
    (file: File) => {
      if (pendingImagesRef.current.length >= MAX_ATTACHMENTS) {
        useNotificationStore.getState().addSystemToast({
          id: `task-comment-attachment-limit-${taskId}`,
          title: translate(panelsTaskCommentsMessages, "attachmentLimitTitle"),
          level: "error",
          description: translate(
            panelsTaskCommentsMessages,
            "attachmentLimitBody",
            { count: MAX_ATTACHMENTS },
          ),
        });
        return;
      }
      return attachComposerFile(file, updateAttachments);
    },
    [taskId, updateAttachments],
  );
  const { dragging, handlePaste, dropProps } = useComposerFileDrop({
    rootRef: composerRef,
    onFile: handleFile,
  });

  const handleRemovePendingImage = useCallback(
    (id: string) => {
      removeComposerAttachment(id, updateAttachments);
    },
    [updateAttachments],
  );

  const handleChange = useCallback(
    (event: ChangeEvent<HTMLTextAreaElement>) => {
      const value = event.target.value;
      setDraft(value);
      resizeField(event.target);
      const match = value.match(/@(\S*)$/);
      if (match) {
        setShowMentions(true);
        setMentionFilter(match[1] ?? "");
        setMentionIndex(0);
      } else {
        setShowMentions(false);
        setMentionFilter("");
      }
    },
    [resizeField],
  );

  const handlePost = useCallback(async () => {
    const content = draftRef.current;
    const images = pendingImagesRef.current;
    if (
      postingRef.current ||
      images.some((image) => image.uploading || image.uploadError) ||
      (!content.trim() && images.length === 0)
    )
      return;
    postingRef.current = true;
    setPosting(true);
    try {
      const imageIds = images
        .filter((image) => image.imageId && !image.uploading)
        .map((image) => image.imageId!);
      const newComment = await postComment(
        projectId,
        taskId,
        content.trim(),
        mentionRef.current?.id ?? null,
        imageIds.length > 0 ? imageIds : undefined,
      );
      onCommentsChange(
        dedupeTaskComments([...commentsRef.current, newComment]),
      );
      if (draftRef.current === content) {
        setDraft("");
        resetField();
      }
      updateAttachments((current) =>
        current.filter((image) => !images.some((sent) => sent.id === image.id)),
      );
      setMention(null);
    } catch (error) {
      useNotificationStore.getState().addSystemToast({
        id: `task-comment-error-${taskId}`,
        title: translate(panelsTaskCommentsMessages, "notSentTitle"),
        level: "error",
        description:
          error instanceof Error
            ? error.message
            : translate(panelsTaskCommentsMessages, "tryAgain"),
      });
    } finally {
      postingRef.current = false;
      setPosting(false);
    }
  }, [onCommentsChange, projectId, resetField, taskId, updateAttachments]);

  const commentDictation = useCommentDictationClaim({
    id: `task-comment:${taskId}`,
    deliverTranscript: (text, images) => {
      const current = draftRef.current.trimEnd();
      setDraft(current ? `${current}\n${text}` : text);
      updateAttachments(
        [...pendingImagesRef.current, ...images].slice(0, MAX_ATTACHMENTS),
      );
      requestAnimationFrame(() => {
        const field = fieldRef.current;
        if (!field) return;
        field.focus();
        resizeField(field);
      });
    },
  });

  const handleKeyDown = useCallback(
    (event: KeyboardEvent<HTMLTextAreaElement>) => {
      if (showMentions && filteredAgents.length > 0) {
        if (event.key === "ArrowDown") {
          event.preventDefault();
          setMentionIndex((index) =>
            Math.min(index + 1, filteredAgents.length - 1),
          );
          return;
        }
        if (event.key === "ArrowUp") {
          event.preventDefault();
          setMentionIndex((index) => Math.max(index - 1, 0));
          return;
        }
        if (event.key === "Enter" || event.key === "Tab") {
          event.preventDefault();
          const selected = filteredAgents[mentionIndex];
          if (selected) selectMention(selected);
          return;
        }
        if (event.key === "Escape") {
          event.preventDefault();
          setShowMentions(false);
          setMentionFilter("");
          return;
        }
      }
      // A second Escape clears the draft; with nothing left to clear,
      // Escape goes on to the pocket.
      if (event.key === "Escape" && draft.trim()) {
        event.preventDefault();
        setDraft("");
        resetField();
        return;
      }
      if (event.key === "Enter" && !event.shiftKey) {
        event.preventDefault();
        if (draft.trim() || pendingImages.length > 0) void handlePost();
      }
    },
    [
      draft,
      filteredAgents,
      handlePost,
      mentionIndex,
      pendingImages.length,
      resetField,
      selectMention,
      showMentions,
    ],
  );

  const canSend =
    !pendingImages.some((image) => image.uploading || image.uploadError) &&
    (draft.trim().length > 0 || pendingImages.length > 0);
  const VoiceIcon = CONCEPT_ICONS.voice;

  return (
    <div
      ref={composerRef}
      className={cn(
        "relative mt-2.5 rounded-[24px]",
        dragging && "ring-2 ring-accent",
      )}
      data-testid="task-pocket-composer"
      onPaste={handlePaste}
      {...dropProps}
    >
      {mention ? (
        <div className="mb-1.5 flex">
          <span className="inline-flex h-6 items-center gap-0.5 rounded-pill bg-[var(--color-surface-selected)] pl-2.5 pr-1 text-appkit-caption font-medium text-primary">
            @{mention.name}
            <Button
              type="button"
              variant="ghost"
              size="iconXs"
              onClick={removeMention}
              aria-label={`Remove @${mention.name}`}
              data-testid="task-pocket-comment-remove-agent"
            >
              <Icons.X className="h-3 w-3" aria-hidden="true" />
            </Button>
          </span>
        </div>
      ) : null}

      <ImagePreviewStrip
        images={pendingImages}
        onRemove={handleRemovePendingImage}
      />

      <div
        className={cn(
          "flex min-h-9 items-end gap-0.5 bg-input-fill py-1.5 pl-4 pr-1.5",
          grown ? "rounded-[24px]" : "rounded-pill",
        )}
        data-testid="task-pocket-comment-field"
      >
        <textarea
          ref={fieldRef}
          value={draft}
          rows={1}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          placeholder={
            mention ? t("replyTo", { name: mention.name }) : t("addComment")
          }
          aria-label={t("comment")}
          className="min-h-6 min-w-0 flex-1 resize-none overflow-hidden bg-transparent py-0.5 text-appkit-body text-primary outline-none placeholder:text-muted"
          data-testid="task-pocket-comment-input"
        />
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          onClick={() => void commentDictation.toggle()}
          disabled={dictationStatus === "processing"}
          aria-pressed={dictationStatus === "recording"}
          aria-label={t("dictate")}
          data-testid="task-pocket-comment-voice"
        >
          <VoiceIcon className="h-4 w-4" aria-hidden="true" />
        </Button>
        <Button
          type="button"
          variant="ghost"
          size="iconSm"
          onClick={() => void handlePost()}
          disabled={!canSend || posting}
          aria-label={t("sendComment")}
          className={canSend ? "text-accent hover:text-accent" : "text-subtle"}
          data-testid="task-pocket-comment-send"
        >
          <Icons.ArrowUp className="h-4 w-4" aria-hidden="true" />
        </Button>
      </div>

      {showMentions ? (
        <div
          className={cn(
            surfaceRoleClassName("menu"),
            "scrollbar-none absolute bottom-full left-0 right-0 z-10 mb-1 max-h-40 overflow-y-auto p-1",
          )}
          data-testid="task-pocket-comment-mentions"
          {...surfaceRoleAttributes("menu", {
            nativeLevel: "root",
            nativeGroup: "menus",
          })}
        >
          {filteredAgents.length === 0 ? (
            <p className="px-2 py-1.5 text-appkit-body text-muted">
              {t("noAgentsFound")}
            </p>
          ) : (
            filteredAgents.map((agent, index) => (
              <button
                key={agent.id}
                type="button"
                onMouseDown={(event) => {
                  event.preventDefault();
                  selectMention(agent);
                }}
                onMouseEnter={() => setMentionIndex(index)}
                className={cn(
                  "flex w-full items-center gap-2 rounded-[12px] px-2 py-1.5 text-left text-appkit-body transition-colors",
                  index === mentionIndex
                    ? "bg-[var(--color-interactive-hover)] text-primary"
                    : "text-muted",
                )}
                data-testid={`task-pocket-comment-mention-${agent.id}`}
              >
                <AgentAvatarPreview
                  styleKey={avatarStyleKey}
                  seed={agent.avatar_seed || agent.name}
                  backgroundColor={denkerAvatarColor(agent.name, project)}
                  size={24}
                />
                <span className="truncate">{agent.name}</span>
              </button>
            ))
          )}
        </div>
      ) : null}
    </div>
  );
}
